import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, User, ExternalLink, Mail, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { usePageMeta } from '@/hooks/usePageMeta';

const audience = [
  {
    title: 'SME and mid-market leaders',
    lead: 'You feel the culture shifting before the numbers show it.',
    body: 'As you scale past a few hundred people, the informal signals that used to tell you how the company felt stop reaching you. CultureLens gives you a weekly read on themes, tensions, and where stated and lived culture diverge, so you can act while the change is still small.',
  },
  {
    title: 'PE-backed portfolio companies',
    lead: 'Value creation depends on people, but culture is the blind spot in the model.',
    body: 'Operating partners and portfolio CEOs get a consistent, confidential read on culture across the hold period. Track leadership trust, alignment, and retention risk the same way across companies, without survey fatigue or another dashboard nobody uses.',
  },
  {
    title: 'HR and CHRO leaders',
    lead: 'You are asked to prove culture with evidence, not anecdotes.',
    body: 'CultureLens turns confidential employee conversations into themes and trends you can bring to the executive table. You keep individual responses private by construction, and still walk in with a clear picture of what your people would say if they were in the room.',
  },
  {
    title: 'Post-merger and integration',
    lead: 'Two cultures rarely become one on the org chart.',
    body: 'During integration, CultureLens shows where the combined organization is actually aligning and where friction is building between legacy teams. Leaders see the gaps early enough to address them, instead of discovering them in regretted departures.',
  },
];

const team = [
  {
    name: 'Dr. Frank Niles',
    role: 'Business Psychologist & CultureLens Advisor',
    bio: 'A business psychologist, speaker, and creator of Communicating with Influence, Dr. Frank Niles has spent two decades studying why some messages change how people think and act while others are forgotten. That work produced his AWE and LAND frameworks and has taken him from Fortune 500 boardrooms to leadership stages worldwide. His insights have been featured by CNN, NPR, Fast Company, and Inc.',
    initials: 'FN',
    isPlaceholder: false,
    linkedin: 'https://www.linkedin.com/in/frankniles',
  },
  {
    name: 'Marcelo Couto, MS',
    role: 'CultureLens Advisor',
    bio: 'A senior HR executive and leadership advisor with 25+ years across the Americas. Former VP HR South America at Ingredion and Partner at IV Talent, Marcelo blends executive practice with academic research, including a Master\'s from FGV-EAESP on the dark side of leadership. Based in Miami and fluent in English, Portuguese, and Spanish.',
    initials: 'MC',
    isPlaceholder: false,
    linkedin: 'https://www.linkedin.com/in/coutomarcelo/',
  },
  {
    name: 'Dean McKenna',
    role: 'CultureLens Advisor',
    bio: 'Founder of IV Talent and a fractional CHRO and board advisor, Dean helps SME and PE-backed companies strengthen culture, leadership, and performance. He pairs senior HR practice with practical technology.',
    initials: 'DM',
    isPlaceholder: false,
    linkedin: 'https://www.linkedin.com/in/dean-m-6069647/',
  },
];

export default function WhoWeAre() {
  usePageMeta({
    title: 'Who We Are — CultureLens by IV Talent',
    description:
      'Meet the team and advisors behind CultureLens — the people building confidential culture insights for mid-market and PE-backed leaders.',
    canonical: 'https://culturelens.org/who-we-are',
  });
  const { ref: aboutRef, isVisible: aboutVisible } = useScrollReveal();
  const { ref: teamRef, isVisible: teamVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: audienceRef, isVisible: audienceVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollReveal({ threshold: 0.2 });
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash?.replace('#', '');
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(t);
  }, [location.hash, location.key]);


  return (
    <div className="min-h-screen bg-cream text-ink font-grotesk antialiased">
      <SiteHeader />

      <main className="pt-24 pb-24">
        <div className="container mx-auto px-6 max-w-5xl">
          {/* Back to Home */}
          <Button variant="ghost" asChild className="mb-8">
            <Link to="/" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to Home
            </Link>
          </Button>

          {/* Section 1: About IV Talent */}
          <section
            id="about"
            ref={aboutRef}
            className={`mb-24 scroll-mt-24 transition-all duration-1000 ${
              aboutVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-[11px] uppercase tracking-[0.18em] font-grotesk text-brand mb-4">
              About Us
            </p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] text-ink mb-8">Built by IV Talent.</h1>
            <div className="max-w-3xl space-y-4 text-lg text-charcoal/75 leading-relaxed font-grotesk">
              <p>
                CultureLens is built by IV Talent, a boutique talent and culture advisory that helps
                small, mid-sized, and PE-backed companies strengthen leadership, culture, and performance.
              </p>
              <p>
                IV Talent blends senior executive HR expertise with practical, always-on technology so
                leaders can see what is really happening in their organization and act on the few moves
                that matter most.
              </p>
              <p className="pt-4">
                <a
                  href="https://www.iv-talent.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-ink font-medium hover:text-brand transition-colors underline underline-offset-4"
                >
                  Visit iv-talent.com
                  <ExternalLink className="h-4 w-4" />
                </a>
              </p>
            </div>
          </section>

          {/* Section 2: Who CultureLens is for */}
          <section id="who-its-for" ref={audienceRef} className="mb-24 scroll-mt-24">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] text-ink mb-3">Who CultureLens is for</h2>
            <p className="text-charcoal/70 text-lg mb-12 max-w-3xl font-grotesk">
              Built for the organizations IV Talent works with every day: growing companies, private equity portfolios, and the HR leaders who carry culture through change.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {audience.map((item, index) => (
                <div
                  key={item.title}
                  className={`p-8 rounded-[4px] border border-hairline bg-cream paper-card transition-all duration-700 ${
                    audienceVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <h3 className="font-display text-xl text-ink mb-2 tracking-[-0.01em]">{item.title}</h3>
                  <p className="text-sm text-brand font-medium mb-3 leading-relaxed font-grotesk">{item.lead}</p>
                  <p className="text-charcoal/70 leading-relaxed font-grotesk">{item.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 3: Talk to one of us */}
          <section id="contact" ref={teamRef} className="scroll-mt-24">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] text-ink mb-3">Talk to one of us</h2>
            <p className="text-charcoal/70 text-lg mb-12 font-grotesk">
              Want to see CultureLens in your organization? Reach out to the person closest to you.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {team.map((member, index) => (
                <div
                  key={member.name}
                  className={`p-8 rounded-[4px] border border-hairline bg-cream paper-card transition-all duration-700 ${
                    teamVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-start gap-5">
                    {/* Avatar */}
                    <div className="shrink-0">
                      {member.isPlaceholder ? (
                        <div className="w-14 h-14 rounded-full bg-cream-warm flex items-center justify-center">
                          <User className="w-7 h-7 text-charcoal/60" />
                        </div>
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-ink flex items-center justify-center">
                          <span className="text-cream font-semibold text-sm font-grotesk">
                            {member.initials}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                      <h3 className="font-display text-xl text-ink mb-1 tracking-[-0.01em]">{member.name}</h3>
                      <p className="text-sm text-brand font-medium mb-3 font-grotesk">{member.role}</p>
                      <p className="text-charcoal/75 leading-relaxed font-grotesk">{member.bio}</p>
                      <div className="flex items-center gap-4 mt-4">
                        {'linkedin' in member && member.linkedin && (
                          <a
                            href={member.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-ink hover:text-brand transition-colors font-grotesk"
                          >
                            <Linkedin className="h-4 w-4" />
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                        {!member.isPlaceholder ? (
                          <a
                            href="mailto:hello@iv-talent.com"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:opacity-80 transition-opacity font-grotesk"
                          >
                            <Mail className="h-3.5 w-3.5" />
                            Contact
                          </a>
                        ) : (
                          <span className="text-sm text-charcoal/60 font-grotesk">Coming soon</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Closing CTA */}
          <section
            ref={ctaRef}
            className={`mt-20 text-center transition-all duration-1000 ${
              ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-charcoal/70 text-lg mb-6 font-grotesk">
              Not sure who to reach? Drop us a line and we will route you to the right person.
            </p>
            <Button asChild className="bg-ink hover:bg-ink-soft text-cream rounded-full px-6 h-11 font-grotesk text-[14px] font-medium">
              <a href="mailto:hello@iv-talent.com">Request access</a>
            </Button>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
